import { useCallback, useEffect, useMemo, useState } from "react";
import { ActivityIndicator, Pressable, ScrollView, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { formatRestTime, REST_FALLBACK_SEC, type Exercise } from "@jackedlogs/shared";
import {
  Icon,
  makeSettingsCardStyles,
  resolveTheme,
  saveExerciseTimerDefaults,
  TextField,
  useExerciseName,
  type UiTheme,
} from "@jackedlogs/ui";
import { ApiError } from "./api";
import { durableWrite } from "./durableWrite";
import { ExercisePickerMount } from "./ExercisePickerMount";
import { ExerciseTimerSheet } from "./logger/ExerciseTimerSheet";
import { fetchAndPersistExercises, loadCachedExercises } from "./logger/exercisesCache";
import { makeTimersScreenStyles } from "./timersScreenStyles";
import { useThemePalette } from "./theme";
import { useProfileDoc } from "./useProfileDoc";

// Settings → Timers on the web console. Global rest + hold defaults
// (profile scalars, get-merge-PUT via useProfileDoc) plus, since 2026-08-19,
// the per-exercise overrides list — the mobile-only note used to say those
// were "native-leaning", but by RW-18 (2026-08-14) every piece it needs
// (ExerciseTimerSheet, the exercise picker, saveExerciseTimerDefaults, the
// catalog cache) already had a web-rnw twin sitting unused for this screen.
// Mirror of apps/mobile/src/screens/RestDefaultsSettingsCard.tsx.

const REST_FALLBACK = REST_FALLBACK_SEC;
const HOLD_FALLBACK = 30;
const MIN_SEC = 5;
const MAX_SEC = 600;

export function TimersScreen() {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { t } = useTranslation();
  const { doc, loading, error, saveField } = useProfileDoc();
  const card = makeSettingsCardStyles(theme);
  const styles = makeTimersScreenStyles(theme);

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={theme.palette.ink2} />
      </View>
    );
  }

  const restGlobal = (doc?.["defaultRestSec"] as number | undefined) ?? REST_FALLBACK;
  const holdGlobal = (doc?.["defaultDurationSec"] as number | undefined) ?? HOLD_FALLBACK;

  return (
    <ScrollView style={styles.flex} contentContainerStyle={styles.body}>
      {error ? <Text style={styles.error}>{error}</Text> : null}
      <View style={card.card}>
        <Text style={card.cardLabel}>
          {t("settings.timerDefaults.globalTitle", { defaultValue: "Global defaults" })}
        </Text>
        <SecondsRow
          theme={theme}
          label={t("settings.timerDefaults.restGlobal", { defaultValue: "Rest timer" })}
          current={doc?.["defaultRestSec"] as number | undefined}
          fallback={REST_FALLBACK}
          onSave={(n) => saveField("defaultRestSec", n)}
        />
        <SecondsRow
          theme={theme}
          label={t("settings.timerDefaults.holdGlobal", { defaultValue: "Hold timer" })}
          current={doc?.["defaultDurationSec"] as number | undefined}
          fallback={HOLD_FALLBACK}
          onSave={(n) => saveField("defaultDurationSec", n)}
        />
      </View>

      <PerExerciseOverridesCard
        theme={theme}
        cardStyles={card}
        restGlobal={restGlobal}
        holdGlobal={holdGlobal}
      />
    </ScrollView>
  );
}

function PerExerciseOverridesCard({
  theme,
  cardStyles,
  restGlobal,
  holdGlobal,
}: {
  theme: UiTheme;
  cardStyles: ReturnType<typeof makeSettingsCardStyles>;
  restGlobal: number;
  holdGlobal: number;
}) {
  const { t } = useTranslation();
  const getExerciseName = useExerciseName();
  const styles = makeTimersScreenStyles(theme);

  const [catalog, setCatalog] = useState<Exercise[] | null>(null);
  const [exSheetSlug, setExSheetSlug] = useState<string | null>(null);
  const [exPickerOpen, setExPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const cached = await loadCachedExercises();
      if (!cancelled && cached) setCatalog(cached);
      try {
        const fresh = await fetchAndPersistExercises();
        if (!cancelled) setCatalog(fresh);
      } catch {
        // Best-effort refresh; keep cached items.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const customRows = useMemo(() => {
    if (!catalog) return [];
    return catalog
      .filter(
        (ex) => typeof ex.defaultRestSec === "number" || typeof ex.defaultDurationSec === "number",
      )
      .sort((a, b) => a.name.en.localeCompare(b.name.en));
  }, [catalog]);

  const exSheetExercise = useMemo(
    () => catalog?.find((x) => x.slug === exSheetSlug) ?? null,
    [catalog, exSheetSlug],
  );

  const saveExercise = useCallback(
    async (slug: string, restSec: number, holdSec: number) => {
      setSaving(true);
      setError(null);
      try {
        await saveExerciseTimerDefaults({
          slug,
          restSec,
          holdSec,
          restGlobal,
          holdGlobal,
          catalog,
          setCatalog,
          durableWrite,
          onSaved: () => void fetchAndPersistExercises().catch(() => undefined),
        });
        setExSheetSlug(null);
      } catch (err) {
        setError(`api_error_${(err as ApiError).status ?? "unknown"}`);
      } finally {
        setSaving(false);
      }
    },
    [catalog, restGlobal, holdGlobal],
  );

  return (
    <View style={cardStyles.card}>
      <View style={styles.customHeader}>
        <Text style={cardStyles.cardLabel}>
          {t("settings.timerDefaults.customLabel", { defaultValue: "Per-exercise overrides" })}
        </Text>
        <Pressable
          accessibilityRole="button"
          onPress={() => {
            setError(null);
            setExPickerOpen(true);
          }}
          style={({ pressed }) => [styles.addBtn, pressed && styles.addBtnPressed]}
        >
          <Text style={styles.addBtnText}>
            {t("settings.timerDefaults.add", { defaultValue: "+ Add" })}
          </Text>
        </Pressable>
      </View>

      {customRows.length === 0 ? (
        <Text style={styles.emptyText}>
          {t("settings.timerDefaults.customEmpty", {
            defaultValue: "No per-exercise overrides yet. Tap + Add.",
          })}
        </Text>
      ) : (
        customRows.map((ex) => (
          <Pressable
            key={ex.slug}
            accessibilityRole="button"
            accessibilityLabel={t("settings.timerDefaults.editA11y", {
              defaultValue: "Edit timer defaults for {{name}}",
              name: getExerciseName(ex.name),
            })}
            onPress={() => {
              setError(null);
              setExSheetSlug(ex.slug);
            }}
            style={({ pressed }) => [styles.customRow, pressed && styles.customRowPressed]}
          >
            <Text style={styles.customRowName} numberOfLines={1}>
              {getExerciseName(ex.name)}
            </Text>
            {typeof ex.defaultRestSec === "number" ? (
              <View style={styles.overrideChip}>
                <Icon name="moon" size={theme.typography.sizes.xs} color={theme.palette.ink2} />
                <Text style={styles.overrideChipText}>{formatRestTime(ex.defaultRestSec)}</Text>
              </View>
            ) : null}
            {typeof ex.defaultDurationSec === "number" ? (
              <View style={styles.overrideChip}>
                <Icon name="timer" size={theme.typography.sizes.xs} color={theme.palette.ink2} />
                <Text style={styles.overrideChipText}>{formatRestTime(ex.defaultDurationSec)}</Text>
              </View>
            ) : null}
            <Icon name="chevron-right" size={theme.typography.sizes.lg} color={theme.palette.ink3} />
          </Pressable>
        ))
      )}

      {error ? <Text style={styles.error}>{error}</Text> : null}

      <ExerciseTimerSheet
        visible={exSheetSlug !== null}
        exercise={exSheetExercise}
        restGlobal={restGlobal}
        holdGlobal={holdGlobal}
        saving={saving}
        onSave={(restSec, holdSec) => {
          if (exSheetSlug) void saveExercise(exSheetSlug, restSec, holdSec);
        }}
        onRemove={() => {
          if (exSheetSlug) void saveExercise(exSheetSlug, restGlobal, holdGlobal);
        }}
        onCancel={() => setExSheetSlug(null)}
      />

      <ExercisePickerMount
        visible={exPickerOpen}
        onClose={() => setExPickerOpen(false)}
        onPickSingle={(ex) => {
          setExPickerOpen(false);
          setExSheetSlug(ex.slug);
        }}
      />
    </View>
  );
}

function SecondsRow({
  theme,
  label,
  current,
  fallback,
  onSave,
}: {
  theme: UiTheme;
  label: string;
  current: number | undefined;
  fallback: number;
  onSave: (value: number | undefined) => Promise<void>;
}) {
  const { t } = useTranslation();
  const styles = makeTimersScreenStyles(theme);
  const [text, setText] = useState(current !== undefined ? String(current) : "");
  const [saving, setSaving] = useState(false);

  // Reflect external loads / saves; the local edit wins while focused is fine
  // for a console (single editor).
  useEffect(() => {
    setText(current !== undefined ? String(current) : "");
  }, [current]);

  const parsed = (() => {
    const n = parseInt(text.trim(), 10);
    return Number.isFinite(n) ? n : NaN;
  })();
  const valid = Number.isNaN(parsed) ? text.trim() === "" : parsed >= MIN_SEC && parsed <= MAX_SEC;
  const dirty = text.trim() !== (current !== undefined ? String(current) : "");

  const save = async () => {
    if (!valid || !dirty) return;
    setSaving(true);
    try {
      await onSave(text.trim() === "" ? undefined : parsed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={styles.row}>
      <View style={styles.rowText}>
        <Text style={styles.rowTitle}>{label}</Text>
        <Text style={styles.rowSub}>
          {current !== undefined
            ? formatRestTime(current)
            : t("settings.timerDefaults.unset", {
                defaultValue: "Not set — {{n}}s",
                n: fallback,
              })}
        </Text>
        {!valid ? (
          <Text style={styles.rowError}>
            {t("settings.timerDefaults.range", {
              defaultValue: "Enter {{min}}–{{max}}s",
              min: MIN_SEC,
              max: MAX_SEC,
            })}
          </Text>
        ) : null}
      </View>
      <TextField
        theme={theme}
        numeric
        align="right"
        invalid={!valid}
        style={styles.input}
        value={text}
        onChangeText={setText}
        placeholder={String(fallback)}
        accessibilityLabel={label}
      />
      <Text style={styles.unit}>s</Text>
      <Pressable
        accessibilityRole="button"
        disabled={!valid || !dirty || saving}
        onPress={() => void save()}
        style={({ pressed }) => [
          styles.saveBtn,
          (!valid || !dirty || saving) && styles.saveBtnDisabled,
          pressed && styles.saveBtnPressed,
        ]}
      >
        {saving ? (
          <ActivityIndicator color={theme.palette.inkOnBrand} />
        ) : (
          <Text style={styles.saveBtnText}>
            {t("mobile.common.save", { defaultValue: "Save" })}
          </Text>
        )}
      </Pressable>
    </View>
  );
}
